import type { RouteAccess } from '@perceptio-llm/features/navigation';
import type { NavFilterChecks, NavItem } from '@/components/shell/nav-model';
import {
  isHeadTeacherMember,
  isTeachingMember,
} from '@/features/auth/model/memberships';
import { toTanStackPath } from '@/routes/portable-route';
import { satisfiesAllAccessRules } from '../model/access-rule';
import {
  navigationGroupRegistry,
  navigationIconRegistry,
} from './navigation-registry';
import type { NavigationLabelKey, WebAppConfig } from '../model/types';

export interface AppNavigationItem extends NavItem {
  routeId: string;
  labelKey?: NavigationLabelKey;
  order: number;
}

export function createAppNavigation(
  config: WebAppConfig,
  checks: NavFilterChecks,
): AppNavigationItem[] {
  const predicates: Readonly<Record<string, () => boolean>> = {
    isTeachingMember: () => isTeachingMember(checks.memberships ?? []),
    isClassMember: () =>
      isHeadTeacherMember(checks.memberships ?? []) ||
      checks.roles.includes('student'),
    isCourseMember: () =>
      isTeachingMember(checks.memberships ?? []) ||
      checks.roles.includes('student'),
    // 日历：教学关系成员、学生、家长，以及校级排课管理者（全校视图）。
    canViewCalendar: () =>
      (checks.memberships ?? []).some(
        ({ entityType }) => entityType === 'course',
      ) ||
      checks.roles.includes('student') ||
      checks.roles.includes('guardian') ||
      checks.can('timetable.manage', checks.scope),
    notUfpAdmin: () => !checks.roles.includes('course_center_admin'),
  };
  const context = {
    can: (capability: string) => checks.can(capability, checks.scope),
    roles: checks.roles,
    predicates,
  };
  const items: AppNavigationItem[] = [];

  for (const binding of config.features) {
    for (const route of binding.descriptor.routes) {
      if (route.scope !== 'workspace' || !route.navigation) continue;
      const webDefault = binding.navigation?.[route.id];
      const override = config.navigation?.overrides?.[route.id];
      if (override?.hidden) continue;
      const rules: readonly (RouteAccess | undefined)[] = [
        route.access,
        route.navigation.access,
      ];
      if (!satisfiesAllAccessRules(rules, context)) continue;
      const iconId = override?.icon ?? route.navigation.icon;
      const groupId = override?.group ?? route.navigation.group;
      const labelKey = override?.labelKey ?? webDefault?.labelKey;
      if (!iconId) throw new Error(`Navigation icon is missing: ${route.id}`);
      const icon = navigationIconRegistry[iconId];
      if (!icon)
        throw new Error(`Unknown navigation icon in ${route.id}: ${iconId}`);
      const group =
        groupId === 'overview' ? undefined : navigationGroupRegistry[groupId];
      if (groupId !== 'overview' && !group) {
        throw new Error(`Unknown navigation group in ${route.id}: ${groupId}`);
      }
      items.push({
        routeId: route.id,
        to:
          route.path === '/'
            ? '/w/$tenantId'
            : `/w/$tenantId${toTanStackPath(route.path)}`,
        label: route.navigation.label ?? route.label,
        ...(labelKey ? { labelKey } : {}),
        icon,
        ...(group ? { group } : {}),
        exact: route.path === '/' || route.id === 'thread.welcome',
        order: override?.order ?? route.navigation.order,
      });
    }
  }
  return items.sort(
    (left, right) =>
      (left.group ?? '').localeCompare(right.group ?? '') ||
      left.order - right.order ||
      left.routeId.localeCompare(right.routeId),
  );
}
